import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search as SearchIcon } from 'lucide-react';
import type { SearchResponseDto } from '@shared/api-contracts';
import { useLocation } from 'wouter';

import { PublicShell } from '@/components/public-shell';
import ClassCard from '@/components/class-card';
import CreatorCard from '@/components/creator-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { fetchJson } from '@/lib/queryClient';
import { useGeographyContext } from '@/hooks/useGeographyContext';
import {
  buildBrowseHref,
  buildReturnToTarget,
  readCanonicalBrowseState,
  updateBrowseUrl,
  useBrowseScrollRestoration,
} from '@/lib/browse-state';

export default function SearchPage() {
  const [location] = useLocation();
  const {
    regionSlug: activeRegionSlug,
    localitySlug: activeLocalitySlug,
    region,
    locality,
    setGeography,
  } = useGeographyContext();
  const locationSearch = typeof window === 'undefined' ? '' : window.location.search;
  const locationState = useMemo(() => readCanonicalBrowseState(locationSearch), [locationSearch]);

  const [query, setQuery] = useState(locationState.q ?? '');
  const [appliedQuery, setAppliedQuery] = useState(locationState.q ?? '');
  const [sort, setSort] = useState(locationState.sort ?? 'popular');

  const scopeRegion = locationState.region ?? activeRegionSlug;
  const scopeLocality = locationState.locality ?? activeLocalitySlug;

  useEffect(() => {
    setQuery(locationState.q ?? '');
    setAppliedQuery(locationState.q ?? '');
    setSort(locationState.sort ?? 'popular');
  }, [location, locationState.q, locationState.sort]);

  useEffect(() => {
    if (scopeRegion !== activeRegionSlug || scopeLocality !== activeLocalitySlug) {
      setGeography(scopeRegion, scopeLocality);
    }
  }, [activeLocalitySlug, activeRegionSlug, scopeLocality, scopeRegion, setGeography]);

  const browseState = useMemo(
    () => ({
      region: scopeRegion,
      locality: scopeLocality,
      q: appliedQuery || undefined,
      sort,
    }),
    [appliedQuery, scopeLocality, scopeRegion, sort]
  );

  useEffect(() => {
    updateBrowseUrl(window.location.pathname, browseState);
  }, [browseState]);

  const currentSearch = buildBrowseHref(window.location.pathname, browseState).split('?')[1] ?? '';
  useBrowseScrollRestoration(
    window.location.pathname,
    currentSearch ? `?${currentSearch}` : '',
    true
  );
  const returnTo = encodeURIComponent(
    buildReturnToTarget(window.location.pathname, window.location.search)
  );

  const searchQuery = useQuery<SearchResponseDto>({
    queryKey: ['search', scopeRegion, scopeLocality, appliedQuery, sort],
    queryFn: async () => {
      if (!scopeRegion) {
        throw new Error('Search region is required');
      }

      const params = new URLSearchParams({
        region: scopeRegion,
        limit: '6',
        sort,
      });
      if (scopeLocality) {
        params.set('locality', scopeLocality);
      }
      if (appliedQuery) {
        params.set('q', appliedQuery);
      }
      return fetchJson<SearchResponseDto>(`/api/search?${params.toString()}`);
    },
    enabled: Boolean(scopeRegion) && appliedQuery.trim().length > 0,
    staleTime: 1000 * 60 * 5,
  });

  const classResults = searchQuery.data?.classes.data ?? [];
  const creatorResults = searchQuery.data?.creators.data ?? [];
  const marketLabel = locality?.name ?? region?.name ?? 'your current market';

  return (
    <PublicShell routeKey="search">
      <section className="hero-gradient border-b border-border/60">
        <div className="page-shell grid gap-8 py-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
          <div>
            <p className="editorial-kicker">Unified search</p>
            <h1 className="section-title mt-4">Search classes and creators in one place.</h1>
            <p className="section-copy mt-5 max-w-2xl">
              Search stays scoped to {marketLabel}. Results are segmented so discovery keeps its
              shape instead of collapsing classes and people into one generic list.
            </p>
          </div>
          <Card className="glass-panel rounded-[2rem] border-border/60 shadow-lg">
            <CardContent className="space-y-4 p-6">
              <div className="flex flex-wrap gap-2">
                <Badge className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                  {scopeRegion}
                </Badge>
                {scopeLocality ? (
                  <Badge variant="outline" className="rounded-full px-3 py-1">
                    {scopeLocality}
                  </Badge>
                ) : null}
              </div>
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      setAppliedQuery(query);
                    }
                  }}
                  placeholder="Search classes, creators, or disciplines"
                  className="touch-target rounded-full pl-10"
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <Button className="rounded-full px-5" onClick={() => setAppliedQuery(query)}>
                  Search
                </Button>
                <select
                  value={sort}
                  onChange={event => setSort(event.target.value)}
                  className="touch-target rounded-full border border-border/70 bg-card px-4 text-sm"
                >
                  <option value="popular">Most popular</option>
                  <option value="newest">Newest</option>
                  <option value="price_asc">Price: low to high</option>
                  <option value="price_desc">Price: high to low</option>
                </select>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="page-section">
        <div className="page-shell space-y-10">
          {appliedQuery.trim().length === 0 ? (
            <Card className="rounded-[1.75rem] border-border/70 shadow-sm">
              <CardContent className="space-y-4 p-6">
                <p className="editorial-kicker">Search prompt</p>
                <h2 className="text-2xl font-semibold">
                  Try a skill, a creator name, or a material.
                </h2>
                <p className="text-sm leading-6 text-muted-foreground">
                  Example searches: “watercolor”, “bread”, “portrait”, “wheel throwing”, “mending”.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <section className="space-y-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="editorial-kicker">Class results</p>
                    <h2 className="mt-3 text-3xl font-semibold">
                      {searchQuery.data?.classes.total ?? 0} classes for “{appliedQuery}”
                    </h2>
                  </div>
                </div>
                {classResults.length > 0 ? (
                  <div className="grid gap-5 grid-cols-[repeat(auto-fill,minmax(18rem,1fr))]">
                    {classResults.map(classItem => (
                      <ClassCard
                        key={classItem.id}
                        id={classItem.id}
                        title={classItem.title}
                        description={classItem.description ?? ''}
                        price={classItem.price}
                        pricingMode={classItem.pricingMode}
                        suggestedPrice={classItem.suggestedPrice}
                        promoEnabled={classItem.promoEnabled}
                        promoPrice={classItem.promoPrice}
                        thumbnailUrl={classItem.thumbnailUrl}
                        category={classItem.category}
                        deliveryMode={classItem.deliveryMode}
                        creator={classItem.creator}
                        spotsLeft={classItem.spotsLeft ?? undefined}
                        localityName={classItem.localityName ?? marketLabel}
                        venue={classItem.venue}
                        detailHref={`/classes/${classItem.id}?returnTo=${returnTo}`}
                        variant="compact"
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyResult
                    title="No classes matched"
                    body="Try a broader term or switch from a locality to the full region."
                  />
                )}
              </section>

              <section className="space-y-5">
                <div>
                  <p className="editorial-kicker">Creator results</p>
                  <h2 className="mt-3 text-3xl font-semibold">
                    {searchQuery.data?.creators.total ?? 0} creators for “{appliedQuery}”
                  </h2>
                </div>
                {creatorResults.length > 0 ? (
                  <div className="grid gap-5 grid-cols-[repeat(auto-fill,minmax(18rem,1fr))]">
                    {creatorResults.map(creator => (
                      <CreatorCard
                        key={creator.id}
                        id={creator.id}
                        displayName={creator.displayName}
                        creatorHeadline={creator.creatorHeadline ?? 'Creative educator'}
                        bio={creator.bio}
                        profileImageUrl={creator.profileImageUrl}
                        isVerified={creator.isVerified}
                        localityName={marketLabel}
                        detailHref={`/creators/${creator.id}?returnTo=${returnTo}`}
                        variant="compact"
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyResult
                    title="No creators matched"
                    body="Try a material, discipline, or neighborhood instead of a full sentence."
                  />
                )}
              </section>
            </>
          )}
        </div>
      </section>
    </PublicShell>
  );
}

function EmptyResult({ title, body }: { title: string; body: string }) {
  return (
    <Card className="rounded-[1.75rem] border-border/70 shadow-sm">
      <CardContent className="space-y-3 p-6">
        <h3 className="text-xl font-semibold">{title}</h3>
        <p className="text-sm leading-6 text-muted-foreground">{body}</p>
      </CardContent>
    </Card>
  );
}
